import React from "react";
import { PackageSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Link from "next/link";
import { renderRichText } from "@/lib/richText";

interface PackageSectionProps {
  blok?: PackageSection & SbBlokData;
}

const PackageSectionComponent: React.FC<PackageSectionProps> = ({ blok }) => {
  if (!blok) return null;

  const { title, subtitle, packages, type } = blok;

  if (type === "iso_27001") {
    return (
      <div className="container text-md-start py-4 py-md-6" id="iso-packages">
        <h2 className="text-center mb-4">{title}</h2>
        {subtitle && <p className="text-center lead col-xl-18 mx-auto mb-5">{subtitle}</p>}
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 gy-3 gx-md-3">
          {packages?.map((pkg) => (
            <div className="col" key={pkg._uid}>
              <div className={`card-cyber-essentials h-100 ${pkg.type === "card-most-popular" ? "card-most-popular" : ""}`}>
                {pkg.type === "card-most-popular" && (
                  <div className="plan-most-popular"><span>Best Value</span></div>
                )}
                <div className="card-cyber-essentials-header d-flex flex-column">
                  <h3 className="card-title text-white">{pkg.title}</h3>
                </div>
                <div className="card-body card-cyber-essentials-body p-3 py-4">
                  {pkg.description && <p>{pkg.description}</p>}
                  {pkg.body && <div dangerouslySetInnerHTML={{ __html: renderRichText(pkg.body) }} />}
                </div>
                {pkg.price && (
                  <div className="lh-1 bg-light text-center p-3">
                    From
                    <div className="h1 mb-0">
                      <span className="card-cyber-essentials-price">£</span>
                      <span className="card-cyber-essentials-price js-card-price">{pkg.price}</span>
                    </div>
                  </div>
                )}
                <div className="card-cyber-essentials-footer">
                  {pkg.cta_button_link && (
                    <Link
                      href={pkg.cta_button_link.cached_url || "#"}
                      className="btn btn-secondary w-100 js-cta-btn"
                    >
                      {pkg.cta_button}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }


  return (
    <div className="bg-oxford-blue" id="vciso-packages">
      <section className="container py-4 py-md-5" {...storyblokEditable(blok)}>
        <h2 className="text-center text-white mb-4">{title}</h2>
        {subtitle && <p className="lead col-12 mx-auto text-center mb-4 mb-xl-5 text-white">{subtitle}</p>}

        <div className="row justify-content-center g-xl-4 pt-4">
          {packages?.map((pkg) => {
            const isFlexPackage = pkg.type === "card-package-flex";
            const isPPType = pkg.type?.endsWith('-pp');

            if (isPPType) {
              return (
                <div
                  key={pkg._uid}
                  className="col-24 col-lg-16 col-xl-8 mb-4 mb-xl-0"
                >
                  <div className={`card card-lighter h-100 card-package ${pkg.type}`}>
                    <div className="card-header p-4 text-center">
                      <h3 className="text-black">{pkg.title}</h3>
                      <h4 className="text-black">{pkg.duration}</h4>
                      <div className="lh-1 text-center p-3">
                        <div className="h1 mb-0">
                          <span className="card-cyber-essentials-price">{pkg.price}</span>
                        </div>
                      </div>
                      <p className="mb-0">{pkg.description}</p>
                    </div>
                    <div className="card-body p-4">
                      {pkg.body && <div dangerouslySetInnerHTML={{ __html: renderRichText(pkg.body) }} />}
                    </div>
                    <div className="card-cyber-essentials-footer">
                      {pkg.cta_button_link && (
                        <Link
                          href={pkg.cta_button_link.cached_url || "#"}
                          className="btn btn-secondary d-block text-center"
                        >
                          {pkg.cta_button}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={pkg._uid}
                className={`col-24 col-lg-16 ${isFlexPackage ? "col-xl-24" : "col-xl-8"} mb-4 mb-xl-0`}
              >
                <div className={`card card-lighter h-100 card-package ${pkg.type}`}>
                  {isFlexPackage ? (
                    <>
                      <div className="d-xl-none">
                        <div className="card-header p-4 text-center">
                          {pkg.icon && <div dangerouslySetInnerHTML={{ __html: pkg.icon }} />}
                          <h3>{pkg.title}</h3>
                          <p className="mb-0">{pkg.description}</p>
                        </div>
                        <div className="card-body p-4">
                          {pkg.body && <div dangerouslySetInnerHTML={{ __html: renderRichText(pkg.body) }} />}
                        </div>
                        {pkg.price && (
                          <div className="lh-1 bg-light text-center p-3">
                            <div className="h1 mb-0">
                              <span className="card-cyber-essentials-price">£</span>
                              <span className="card-cyber-essentials-price">{pkg.price}</span>
                            </div>
                            {pkg.duration && <div>{pkg.duration}</div>}
                          </div>
                        )}
                        <div className="card-cyber-essentials-footer">
                          {pkg.cta_button_link && (
                            <Link
                              href={pkg.cta_button_link.cached_url || "#"}
                              className="btn btn-secondary d-block text-center"
                            >
                              {pkg.cta_button}
                            </Link>
                          )}
                        </div>
                      </div>

                      <div className="row g-0 d-none d-xl-flex">
                        <div className="col-8 col-xl-4 text-center text-dark py-4 package-icon-column">
                          {pkg.icon && <div dangerouslySetInnerHTML={{ __html: pkg.icon }} />}
                        </div>
                        <div className="col-16 col-xl-14 p-4">
                          <h3>{pkg.title}</h3>
                          <p className="text-muted">{pkg.description}</p>
                          {pkg.body && (
                            <div
                              className="text-dark lead mb-0"
                              dangerouslySetInnerHTML={{ __html: renderRichText(pkg.body) }}
                            />
                          )}
                        </div>
                        <div className="col-24 col-xl-6 d-flex flex-column justify-content-between">
                          {pkg.price && (
                            <div className="lh-1 bg-light text-center p-3 flex-grow-1 d-flex flex-column justify-content-center">
                              <div className="h1 mb-0">
                                <span className="card-cyber-essentials-price">£</span>
                                <span className="card-cyber-essentials-price">{pkg.price}</span>
                              </div>
                              {pkg.duration && <div>{pkg.duration}</div>}
                            </div>
                          )}
                          <div>
                            {pkg.cta_button_link && (
                              <Link
                                href={pkg.cta_button_link.cached_url || "#"}
                                className="btn btn-secondary d-block text-center"
                              >
                                {pkg.cta_button}
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="card-header p-4 text-center">
                        {pkg.icon && <div dangerouslySetInnerHTML={{ __html: pkg.icon }} />}
                        <h3>{pkg.title}</h3>
                        <p className="mb-0">{pkg.description}</p>
                      </div>
                      <div className="card-body p-4">
                        {pkg.body && <div dangerouslySetInnerHTML={{ __html: renderRichText(pkg.body) }} />}
                      </div>
                      {pkg.price && (
                        <div className="lh-1 bg-light text-center p-3">
                          <div className="h1 mb-0">
                            <span className="card-cyber-essentials-price">£</span>
                            <span className="card-cyber-essentials-price">{pkg.price}</span>
                          </div>
                          {pkg.duration && <div>{pkg.duration}</div>}
                        </div>
                      )}
                      <div className="card-cyber-essentials-footer">
                        {pkg.cta_button_link && (
                          <Link
                            href={pkg.cta_button_link.cached_url || "#"}
                            className="btn btn-secondary d-block text-center"
                          >
                            {pkg.cta_button}
                          </Link>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default PackageSectionComponent;